// Branch-scoped permission matcher.
//
// The app's single source of truth for "can the current user do X on module
// Y" is the payload cached by `fetchAndCacheBranchPermissions()` in
// `localStorage['branchPermissions']`:
//
//   {
//     branchId: 1,
//     type: 'COMPANY',
//     permissions: [
//       { id, companyId, branchId, moduleId, permission, moduleName, moduleSlug },
//       ...
//     ],
//     fetchedAt: 1793336002125,
//   }
//
// This module turns that flat list into lookups and exposes
// `hasPermission(module, action)` — the ONE helper every call site uses.
//
// Design notes:
//
//   * Strict actions. A row grants exactly the action it names — there is no
//     tier ladder. `update` does NOT imply `create`, and `create` does NOT
//     imply `view`. A left-panel tab needs an explicit `view` row.
//
//   * Action aliases. The role builder stores `read` for View and `edit` for
//     Update (see `src/pages/usersRoles/constant.js`), while the branch
//     endpoint sends `view` / `update`. Both spellings normalise to the
//     canonical verb.
//
//   * Deny by default. No cached record (or a record belonging to another
//     branch) means *no* permission. The record is written during branch
//     selection and refreshed on every branch switch, so a missing record
//     means the branch's permissions were never loaded.
//
//   * No COMPANY bypass. The branch endpoint already returns the effective
//     per-module list for the current user in the current branch, so the
//     payload — not the account type — decides.
//
//   * Matching order per module: `moduleSlug` → `moduleId` → `moduleName`.
//     The id/name fallbacks matter because some backend module rows ship a
//     null `moduleSlug`.
//
// `module` may be:
//
//   * an app module key (`MODULE_NAME.ROLE`) — resolved through
//     `PERMISSION_MODULE` in `src/modules/index.js`,
//   * a raw backend slug string (`'roles-and-permission'`),
//   * a numeric backend module id,
//   * `{ slug, moduleId, name }`.
//
// The helpers are pure and synchronous, so they work inside JSX, inside
// `.filter(...)` over row-action menus, and in plain (non-React) code.

import { PERMISSIONS } from '../constants/permission'
import { PERMISSION_MODULE } from '../modules'

import { permissionStorage } from './permissionStorage'
import { getSelectedBranchMeta } from './userStorage'

// Canonical action per accepted spelling. Unknown verbs pass through
// untouched so an unexpected backend action is still matchable.
const ACTION_ALIASES = {
  view: PERMISSIONS.VIEW,
  read: PERMISSIONS.VIEW,
  create: PERMISSIONS.CREATE,
  update: PERMISSIONS.UPDATE,
  edit: PERMISSIONS.UPDATE,
  modify: PERMISSIONS.UPDATE,
  delete: PERMISSIONS.DELETE,
  remove: PERMISSIONS.DELETE,
  upload: PERMISSIONS.UPLOAD,
}

function normalizeAction(raw) {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase()
  if (!value) return null
  return ACTION_ALIASES[value] ?? value
}

function addTo(index, key, action) {
  if (key === undefined || key === null || key === '') return
  const k = String(key).toLowerCase()
  if (!index.has(k)) index.set(k, new Set())
  index.get(k).add(action)
}

// Build the three lookup maps the matcher consults.
function buildIndex(record) {
  const bySlug = new Map()
  const byModuleId = new Map()
  const byName = new Map()

  const rows = extractRows(record)
  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return
    // Soft-deleted rows (the backend keeps `deletedAt` on permission
    // rows) must not grant anything.
    if (row.deletedAt) return
    const action = normalizeAction(row.permission)
    if (!action) return
    addTo(bySlug, row.moduleSlug, action)
    addTo(byModuleId, row.moduleId, action)
    addTo(byName, row.moduleName, action)
  })

  return {
    bySlug,
    byModuleId,
    byName,
    branchId: record?.branchId ?? null,
    type: record?.type ?? null,
  }
}

// Pull the permission rows out of the cached record. The canonical shape is
// `{ branchId, type, permissions: [...] }`, but a bare array of rows — or a
// single row — is accepted too: a payload cached without the wrapper would
// otherwise make every lookup silently deny.
function extractRows(record) {
  if (Array.isArray(record)) return record
  if (Array.isArray(record?.permissions)) return record.permissions
  // A single row: it identifies a module and carries an action.
  if (record && typeof record === 'object' && record.permission) return [record]
  return []
}

// The record is re-parsed from localStorage on every read, so memoise the
// index against the record's identity (branch + fetch timestamp + row count).
let cachedSignature = null
let cachedIndex = null

function getIndex(record) {
  const rows = extractRows(record).length
  const signature = `${record?.branchId ?? ''}|${record?.fetchedAt ?? ''}|${rows}`
  if (signature === cachedSignature && cachedIndex) return cachedIndex
  cachedSignature = signature
  cachedIndex = buildIndex(record)
  return cachedIndex
}

/**
 * The cached branch-permission record, but only when it belongs to the
 * branch the user currently has selected. A record left over from a previous
 * branch is treated as "no record".
 *
 * @returns {object | null}
 */
export function getBranchPermissionRecord() {
  const record = permissionStorage.getPermissions()
  if (!record) return null

  const meta = getSelectedBranchMeta()
  const currentId = meta?.id ?? meta?.uuid ?? null
  if (currentId !== null && record.branchId !== null && record.branchId !== undefined) {
    if (String(record.branchId) !== String(currentId)) return null
  }

  return record
}

/**
 * The lookup maps for the current branch's permissions.
 *
 * @returns {{ bySlug: Map<string, Set<string>>, byModuleId: Map<string, Set<string>>, byName: Map<string, Set<string>>, branchId: any, type: string | null } | null}
 */
export function getBranchPermissionIndex() {
  const record = getBranchPermissionRecord()
  if (!record) return null
  return getIndex(record)
}

// Resolve a module argument into the concrete candidates the index holds.
function resolveCandidates(module) {
  const slugs = []
  const ids = []
  const names = []

  if (module === undefined || module === null || module === '') {
    return { slugs, ids, names }
  }

  if (typeof module === 'object') {
    if (module.slug) slugs.push(String(module.slug))
    const id = module.moduleId ?? module.id
    if (id !== undefined && id !== null && id !== '') ids.push(id)
    if (module.name) names.push(String(module.name))
    return { slugs, ids, names }
  }

  if (typeof module === 'number') {
    ids.push(module)
    return { slugs, ids, names }
  }

  const key = String(module)
  const mapped = PERMISSION_MODULE[key]
  if (mapped) {
    if (mapped.slug) slugs.push(mapped.slug)
    if (mapped.moduleId !== undefined && mapped.moduleId !== null) ids.push(mapped.moduleId)
    return { slugs, ids, names }
  }

  // Not an app module key — treat the string as a raw backend slug.
  slugs.push(key)
  return { slugs, ids, names }
}

/**
 * The permission check used everywhere in the app.
 *
 * @param {string | number | object} module App module key
 *   (`MODULE_NAME.USER_MANAGEMENT`), a raw backend slug, a backend module
 *   id, or `{ slug, moduleId, name }`.
 * @param {string} [action=PERMISSIONS.VIEW] `view` | `create` | `update` |
 *   `delete` | `upload`.
 * @returns {boolean} `false` when the branch permission record is missing,
 *   stale, or simply doesn't grant the action.
 */
export function hasPermission(module, action = PERMISSIONS.VIEW) {
  const index = getBranchPermissionIndex()
  if (!index) return false

  const target = normalizeAction(action)
  if (!target) return false

  const { slugs, ids, names } = resolveCandidates(module)

  for (const slug of slugs) {
    const granted = index.bySlug.get(String(slug).toLowerCase())
    if (granted && granted.has(target)) return true
  }

  for (const id of ids) {
    const granted = index.byModuleId.get(String(id))
    if (granted && granted.has(target)) return true
  }

  for (const name of names) {
    const granted = index.byName.get(String(name).toLowerCase())
    if (granted && granted.has(target)) return true
  }

  return false
}

/**
 * `true` when the user holds `action` on **any** of the listed modules.
 * Mirrors the OR-semantics the left panel uses for grouped nav items.
 *
 * @param {Array<string | number | object>} modules
 * @param {string} [action=PERMISSIONS.VIEW]
 * @returns {boolean}
 */
export function hasAnyPermission(modules, action = PERMISSIONS.VIEW) {
  if (!Array.isArray(modules)) return false
  return modules.some((m) => hasPermission(m, action))
}

/**
 * The backend slug a module resolves to — handy for debugging a permission
 * that doesn't behave the way it was expected to.
 *
 * @param {string} module
 * @returns {string | null}
 */
export function getPermissionModuleSlug(module) {
  const { slugs } = resolveCandidates(module)
  return slugs[0] ?? null
}
